/**
 * Grouping rows by an axis that is not the tree they live in — `KLQ-20260904-004` §A.
 *
 * **It is a bucketing, not a tree builder, and that is the whole reason it can be shared.**
 * Three surfaces pivot (drawings, tasks, reviews) and their rows have nothing in common: a
 * `TreeNode`, a `PlanRow` and a `ReviewRow` are three unions with three different container
 * shapes. What they *do* share is the rule — which leaf lands in which bucket, how buckets
 * nest, what happens to a leaf that answers none, and what happens to one that answers several.
 * So this module owns exactly that and never constructs a row; each surface turns a
 * `PivotBucket` into its own heading.
 *
 * Retro rule 8, on the nose: `groupByObject` was the first appearance of this rule and lived
 * inside the drawings feature. The second appearance is when it becomes an object, rather than
 * the third being when somebody notices there are three.
 */

/** One bucket a leaf may land in. `parentId` nests them — objects nest, statuses do not. */
export interface PivotGroup {
  readonly id: string;
  readonly label: string;
  readonly parentId?: string | null | undefined;
}

export interface PivotSpec<Leaf> {
  /** Every bucket that may exist, in the order they should be drawn. */
  readonly groups: readonly PivotGroup[];
  /**
   * Which buckets this leaf belongs to — **zero, one or several** (`KLQ-20260904-004` §B).
   *
   * Zero is the unassigned bucket. Several is a task producing drawings under two objects, and
   * it is drawn under both: the alternative buckets ("Viac objektov", "only when they agree")
   * hide exactly the cross-object work somebody pivoted in order to find.
   */
  readonly keysOf: (leaf: Leaf) => readonly string[];
  /** How many subjects this leaf is worth in a heading's count. Default: one. */
  readonly weightOf?: ((leaf: Leaf) => number) | undefined;
  readonly unassignedLabel: string;
}

export interface PivotBucket<Leaf> {
  readonly id: string;
  readonly label: string;
  /** The leaves that landed here, in the order they were given. */
  readonly leaves: readonly Leaf[];
  readonly children: readonly PivotBucket<Leaf>[];
  /** This bucket and everything below it — what a heading prints. */
  readonly count: number;
  /** True for the *no bucket* heading, which is drawn last and never dropped. */
  readonly unassigned: boolean;
}

/** The id of the unassigned bucket. Prefixed like every other, so no leaf id can collide. */
export const UNASSIGNED = "pivot:unassigned";

/**
 * A node id for a leaf **as drawn inside one bucket**.
 *
 * A duplicated row needs a distinct id, because row ids are what the selection, the cell grid
 * and the bulk endpoints are keyed on. `::` is the separator and `leafIdOf` is the only reader
 * of it: no consumer writes a `split`, and none writes a regex — the ayon bug this repo already
 * declined to inherit (`KLQ-20260828-001` §A.1) is the same bug one module over.
 */
export function pivotNodeId(bucketId: string, leafId: string): string {
  return `${bucketId}::${leafId}`;
}

/**
 * The subject behind a row id, pivoted or not.
 *
 * Total on purpose: a caller hands in whatever the table gave it and gets back a real id, so
 * the unwrapping is one call at the boundary rather than a branch on the current axis in every
 * action.
 */
export function leafIdOf(rowId: string): string {
  const at = rowId.indexOf("::");
  return at === -1 ? rowId : rowId.slice(at + 2);
}

/**
 * Group `leaves` into the buckets `spec` describes.
 *
 * Buckets with nothing beneath them are absent — an SO that no drawing mentions is not a
 * heading, it is an object somebody has not filed against yet. The unassigned bucket is last
 * whenever any leaf answered nothing, which is what keeps the pivot from silently eating rows
 * (`KLQ-20260820-006` §0.2).
 */
export function bucketBy<Leaf>(
  leaves: readonly Leaf[],
  spec: PivotSpec<Leaf>,
): readonly PivotBucket<Leaf>[] {
  const weightOf = spec.weightOf ?? (() => 1);

  const held = new Map<string, Leaf[]>();
  const orphans: Leaf[] = [];
  const known = new Set(spec.groups.map((group) => group.id));

  for (const leaf of leaves) {
    // A key naming a bucket this spec does not have is the same answer as no key at all: an
    // object that has since been archived out of the picker still has drawings filed under it,
    // and they belong in *No object* rather than nowhere (`KLQ-20260820-006` §0.3).
    const keys = spec.keysOf(leaf).filter((key) => known.has(key));
    if (keys.length === 0) {
      orphans.push(leaf);
      continue;
    }
    for (const key of new Set(keys)) {
      const bucket = held.get(key);
      if (bucket === undefined) held.set(key, [leaf]);
      else bucket.push(leaf);
    }
  }

  const childrenOf = new Map<string | null, PivotGroup[]>();
  for (const group of spec.groups) {
    const key = group.parentId ?? null;
    const bucket = childrenOf.get(key);
    if (bucket === undefined) childrenOf.set(key, [group]);
    else bucket.push(group);
  }

  const build = (parentId: string | null): PivotBucket<Leaf>[] => {
    const out: PivotBucket<Leaf>[] = [];
    for (const group of childrenOf.get(parentId) ?? []) {
      const own = held.get(group.id) ?? [];
      const children = build(group.id);
      if (own.length === 0 && children.length === 0) continue;
      out.push({
        id: group.id,
        label: group.label,
        leaves: own,
        children,
        count:
          own.reduce((total, leaf) => total + weightOf(leaf), 0) +
          children.reduce((total, child) => total + child.count, 0),
        unassigned: false,
      });
    }
    return out;
  };

  const buckets = build(null);
  if (orphans.length === 0) return buckets;
  return [
    ...buckets,
    {
      id: UNASSIGNED,
      label: spec.unassignedLabel,
      leaves: orphans,
      children: [],
      count: orphans.reduce((total, leaf) => total + weightOf(leaf), 0),
      unassigned: true,
    },
  ];
}
